import React, { useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Planet data for the cards - Image URLs set to your specified filenames!
const planetTypesData = [
  {
    id: 'mercury',
    name: 'Mercury',
    shortDescription: 'The smallest planet in our solar system and closest to the Sun.',
    longDescription: 'Mercury is the smallest planet in our solar system and the closest to the Sun. It has a heavily cratered surface, similar to Earth\'s Moon, and experiences extreme temperature variations, from scorching hot days to freezing cold nights. It has no significant atmosphere to trap heat or protect it from meteoroid impacts.',
    imageUrl: '4.png', // Placeholder image
    color: 'bg-gray-700',
    borderColor: 'border-gray-500/50'
  },
  {
    id: 'venus',
    name: 'Venus',
    shortDescription: 'Earth\'s "sister planet" with a toxic atmosphere and scorching surface.',
    longDescription: 'Venus is often called Earth\'s "sister planet" due to its similar size and mass, but its environment is drastically different. It has a thick, toxic atmosphere of carbon dioxide and is shrouded in yellowish clouds of sulfuric acid, trapping heat in a runaway greenhouse effect. This makes Venus the hottest planet in our solar system, with surface temperatures hot enough to melt lead.',
    imageUrl: '5.png', // Placeholder image
    color: 'bg-orange-700',
    borderColor: 'border-orange-500/50'
  },
  {
    id: 'earth',
    name: 'Earth',
    shortDescription: 'Our home planet, unique for its liquid water and diverse life.',
    longDescription: 'Earth is the third planet from the Sun and the only astronomical object known to harbor life. Its unique combination of liquid water on its surface, a protective atmosphere, and a stable temperature range makes it ideal for supporting a vast array of ecosystems. Our planet also has a strong magnetic field that shields us from harmful solar radiation.',
    imageUrl: '6.png', // Placeholder image
    color: 'bg-blue-600',
    borderColor: 'border-blue-400/50'
  },
  {
    id: 'mars',
    name: 'Mars',
    shortDescription: 'The "Red Planet," known for its iron-rich surface and polar ice caps.',
    longDescription: 'Mars is the fourth planet from the Sun and is often called the "Red Planet" due to the iron oxide prevalent on its surface. It has a thin atmosphere, polar ice caps, and evidence of ancient riverbeds, suggesting it once had liquid water. Mars is a prime target for scientific exploration, with rovers and orbiters searching for signs of past or present life.',
    imageUrl: '7.png', // Placeholder image
    color: 'bg-red-700',
    borderColor: 'border-red-400/50'
  },
  {
    id: 'jupiter',
    name: 'Jupiter',
    shortDescription: 'The largest planet, a gas giant with a famous Great Red Spot.',
    longDescription: 'Jupiter is the largest planet in our solar system, a massive gas giant composed primarily of hydrogen and helium. It is famous for its Great Red Spot, a colossal storm larger than Earth that has raged for centuries. Jupiter has a strong magnetic field, numerous moons (including the four Galilean moons), and faint ring systems.',
    imageUrl: '8.png', // Placeholder image
    color: 'bg-yellow-800',
    borderColor: 'border-yellow-500/50'
  },
  {
    id: 'saturn',
    name: 'Saturn',
    shortDescription: 'Known for its spectacular and complex system of rings.',
    longDescription: 'Saturn is the sixth planet from the Sun and the second-largest in our solar system, best known for its stunning and intricate system of rings. These rings are made of billions of small particles of ice and rock. Like Jupiter, Saturn is a gas giant, predominantly composed of hydrogen and helium, and it boasts a large number of moons, including the enigmatic Titan.',
    imageUrl: '9.png', // Placeholder image
    color: 'bg-amber-600',
    borderColor: 'border-amber-400/50'
  },
  {
    id: 'uranus',
    name: 'Uranus',
    shortDescription: 'An ice giant that rotates on its side, appearing bluish-green.',
    longDescription: 'Uranus is the seventh planet from the Sun and is an ice giant, composed largely of water, methane, and ammonia ices over a small rocky core. Its unique feature is that it rotates on its side, meaning its poles are where most other planets have their equators. This gives it extreme seasonal variations. It has a faint ring system and numerous moons, appearing bluish-green due to methane in its atmosphere.',
    imageUrl: '10.png', // Placeholder image
    color: 'bg-teal-600',
    borderColor: 'border-teal-400/50'
  },
  {
    id: 'neptune',
    name: 'Neptune',
    shortDescription: 'The farthest planet, a cold, dark ice giant with strong winds.',
    longDescription: 'Neptune is the eighth and farthest known planet from the Sun in our solar system. It is another ice giant, similar in composition to Uranus, but with even stronger winds – the fastest in the solar system. Neptune has a dynamic atmosphere with prominent storms, a faint ring system, and 14 known moons, the largest being Triton. It appears deep blue due to the presence of methane.',
    imageUrl: '11.png', // Placeholder image
    color: 'bg-blue-800',
    borderColor: 'border-blue-500/50'
  },
  {
    id: 'pluto',
    name: 'Pluto',
    shortDescription: 'A famous dwarf planet in the Kuiper Belt, once considered the ninth planet.',
    longDescription: 'Pluto is a dwarf planet located in the Kuiper Belt, a region of icy bodies beyond Neptune. For many decades, it was considered the ninth planet, but it was reclassified in 2006 due to new criteria for planethood. Pluto has a surprisingly complex surface with mountains, plains, and valleys, and a thin atmosphere that freezes and collapses as it moves away from the Sun.',
    imageUrl: '12.png', // Placeholder image
    color: 'bg-purple-800',
    borderColor: 'border-purple-500/50'
  },
];

// Sub-component for a single planet card
function PlanetCard({ planet, isExpanded, onToggleExpand }) {
  return (
    <motion.div
      layout
      className={`relative p-6 border rounded-lg shadow-xl ${planet.borderColor} ${isExpanded ? 'col-span-full' : ''}`}
      transition={{ layout: { duration: 0.5, type: "spring", damping: 20, stiffness: 100 } }}
      style={{ overflow: 'hidden' }}
    >
      {/* Background Image Container */}
      <div
        className="absolute inset-0 transition-opacity duration-300 bg-center bg-cover rounded-lg opacity-60 group-hover:opacity-50"
        style={{ backgroundImage: `url(${planet.imageUrl})` }}
      ></div>
      {/* Content Overlay - ensures text is readable over image */}
      <div className="relative z-10 flex flex-col h-full">
        <motion.h3 layout="position" className="mb-3 text-2xl font-bold text-white-300">
          {planet.name}
        </motion.h3>
        <AnimatePresence mode="wait">
          {!isExpanded && (
            <motion.p
              key="short-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex-grow text-white-400"
            >
              {planet.shortDescription}
            </motion.p>
          )}
          {isExpanded && (
            <motion.p
              key="long-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-grow mt-4 text-white-400"
            >
              {planet.longDescription}
            </motion.p>
          )}
        </AnimatePresence>
        <motion.button
          className="self-start px-4 py-2 mt-6 font-semibold text-white transition-colors duration-300 bg-indigo-500 rounded-full hover:bg-indigo-600"
          onClick={() => onToggleExpand(planet.id)}
          layout="position"
        >
          {isExpanded ? 'View Less' : 'View More'}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function PlanetPage() {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleToggleExpand = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  return (
    <PageWrapper>
      <motion.div
        className="flex flex-col items-center justify-center min-h-screen p-8 text-white bg-gradient-to-br from-indigo-900 to-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h1
          className="mb-6 text-6xl font-extrabold text-teal-400 drop-shadow-lg"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
        >
          Explore Our Planetary Neighbors
        </motion.h1>
        <motion.p
          className="max-w-3xl mb-12 text-xl text-center text-gray-300"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 100, damping: 10 }}
        >
          Discover the unique characteristics of the planets in our solar system and beyond. Click "View More" to dive deeper into each world.
        </motion.p>
        <motion.div
          layout
          className="grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          {planetTypesData.map((planet) => (
            <PlanetCard
              key={planet.id}
              planet={planet}
              isExpanded={expandedCardId === planet.id}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </motion.div>
        <Link to="/explore" className="px-8 py-3 mt-16 text-xl font-semibold text-white no-underline transition-all duration-300 ease-in-out bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 hover:shadow-blue-500/50">
          Back to Explore
        </Link>
      </motion.div>
    </PageWrapper>
  );
}